import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { FaHome, FaSearch, FaArrowLeft } from "react-icons/fa";

const NotFound = () => {
  return (
    <>
      <Helmet>
        <title>404 - Page Not Found | Medichy</title>
        <meta
          name="description"
          content="The page you're looking for cannot be found. Return to Medichy's homepage to discover our medical equipment solutions."
        />
      </Helmet>
      <TheNotFoundPage>
        <div className="container">
          <div className="notfound-card">
            <div className="error-code">
              <span className="digit">4</span>
              <span className="zero">0</span>
              <span className="digit">4</span>
            </div>

            <h1>Oops! Page Not Found</h1>
            <p>
              The page you are looking for might have been removed, had its name
              changed, or is temporarily unavailable.
            </p>

            <div className="button-group">
              <Link to="/" className="btn-primary">
                <FaHome />
                Back to Home
              </Link>
              <Link to="/products" className="btn-secondary">
                <FaSearch />
                Browse Products
              </Link>
              <button
                onClick={() => window.history.back()}
                className="btn-outline"
              >
                <FaArrowLeft />
                Go Back
              </button>
            </div>

            <div className="help-section">
              <h4>Need help finding something?</h4>
              <div className="help-links">
                <Link to="/products">Medical Equipment</Link>
                <Link to="/startups">Startup Solutions</Link>
                <Link to="/oems">OEM Services</Link>
                <Link to="/contact-us">Contact Support</Link>
              </div>
            </div>
          </div>
        </div>
      </TheNotFoundPage>
    </>
  );
};

const TheNotFoundPage = styled.div`
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  .container {
    width: 100%;
    max-width: 650px;
    margin: 0 auto;
  }

  .notfound-card {
    background: white;
    border-radius: 24px;
    padding: 3rem 2.5rem;
    text-align: center;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.1);
    animation: fadeInUp 0.5s ease;

    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .error-code {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      margin-bottom: 1.5rem;
      font-size: 6rem;
      font-weight: 800;

      .digit {
        color: #1a1a1a;
        text-shadow: 4px 4px 0 #e2e8f0;
      }

      .zero {
        color: #3182ce;
        text-shadow: 4px 4px 0 #bfdbfe;
        position: relative;

        &::before {
          content: "◌";
          position: absolute;
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          font-size: 1rem;
          color: #3182ce;
          opacity: 0.5;
        }
      }
    }

    h1 {
      font-size: 2rem;
      font-weight: 700;
      color: #1a1a1a;
      margin-bottom: 1rem;
    }

    p {
      font-size: 1rem;
      color: #4a5568;
      line-height: 1.6;
      margin-bottom: 2rem;
      max-width: 450px;
      margin-left: auto;
      margin-right: auto;
    }

    .button-group {
      display: flex;
      gap: 1rem;
      justify-content: center;
      margin-bottom: 2.5rem;
      flex-wrap: wrap;

      .btn-primary {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        background: linear-gradient(135deg, #3182ce 0%, #2c5282 100%);
        color: white;
        padding: 0.875rem 1.5rem;
        font-size: 0.9rem;
        font-weight: 600;
        border-radius: 10px;
        text-decoration: none;
        transition: all 0.3s ease;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(49, 130, 206, 0.3);
        }
      }

      .btn-secondary {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        background: white;
        color: #3182ce;
        border: 2px solid #3182ce;
        padding: 0.875rem 1.5rem;
        font-size: 0.9rem;
        font-weight: 600;
        border-radius: 10px;
        text-decoration: none;
        transition: all 0.3s ease;

        &:hover {
          background: #3182ce;
          color: white;
          transform: translateY(-2px);
        }
      }

      .btn-outline {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        background: transparent;
        color: #4a5568;
        border: 2px solid #e2e8f0;
        padding: 0.875rem 1.5rem;
        font-size: 0.9rem;
        font-weight: 600;
        border-radius: 10px;
        text-decoration: none;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          border-color: #3182ce;
          color: #3182ce;
          transform: translateY(-2px);
        }
      }
    }

    .help-section {
      border-top: 1px solid #e2e8f0;
      padding-top: 2rem;

      h4 {
        font-size: 1rem;
        font-weight: 600;
        color: #1a1a1a;
        margin-bottom: 1rem;
      }

      .help-links {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
        justify-content: center;

        a {
          color: #3182ce;
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 500;
          transition: all 0.3s ease;
          padding: 0.25rem 0.5rem;

          &:hover {
            color: #2c5282;
            text-decoration: underline;
          }
        }
      }
    }
  }

  /* Media Queries */
  @media screen and (max-width: 1366px) {
    padding: 2rem 1.5rem;

    .notfound-card {
      padding: 2.5rem 2rem;
    }
  }

  @media screen and (max-width: 1200px) {
    .notfound-card {
      max-width: 550px;
    }
  }

  @media screen and (max-width: 900px) {
    padding: 2rem 1.5rem;

    .notfound-card {
      padding: 2rem 1.5rem;

      .error-code {
        font-size: 5rem;
        gap: 0.3rem;
      }

      h1 {
        font-size: 1.75rem;
      }

      p {
        font-size: 0.95rem;
      }

      .button-group {
        gap: 0.8rem;

        .btn-primary,
        .btn-secondary,
        .btn-outline {
          padding: 0.75rem 1.25rem;
          font-size: 0.85rem;
        }
      }
    }
  }

  @media screen and (max-width: 600px) {
    padding: 1.5rem;

    .notfound-card {
      padding: 1.75rem 1.25rem;

      .error-code {
        font-size: 4rem;
        margin-bottom: 1rem;
      }

      h1 {
        font-size: 1.5rem;
      }

      p {
        font-size: 0.9rem;
        margin-bottom: 1.5rem;
      }

      .button-group {
        gap: 0.75rem;
        margin-bottom: 2rem;

        .btn-primary,
        .btn-secondary,
        .btn-outline {
          padding: 0.65rem 1rem;
          font-size: 0.8rem;
        }
      }

      .help-section {
        padding-top: 1.5rem;

        h4 {
          font-size: 0.9rem;
        }

        .help-links {
          gap: 0.75rem;

          a {
            font-size: 0.85rem;
          }
        }
      }
    }
  }

  @media screen and (max-width: 420px) {
    padding: 1rem;

    .notfound-card {
      padding: 1.5rem 1rem;

      .error-code {
        font-size: 3.5rem;
      }

      h1 {
        font-size: 1.35rem;
      }

      p {
        font-size: 0.85rem;
      }

      .button-group {
        flex-direction: column;
        width: 100%;

        .btn-primary,
        .btn-secondary,
        .btn-outline {
          width: 100%;
          justify-content: center;
          padding: 0.65rem;
        }
      }

      .help-section {
        .help-links {
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;

          a {
            font-size: 0.85rem;
          }
        }
      }
    }
  }

  @media screen and (max-width: 350px) {
    padding: 0.75rem;

    .notfound-card {
      padding: 1.25rem 0.875rem;

      .error-code {
        font-size: 3rem;
      }

      h1 {
        font-size: 1.2rem;
      }

      p {
        font-size: 0.8rem;
      }

      .button-group {
        gap: 0.6rem;
        margin-bottom: 1.5rem;

        .btn-primary,
        .btn-secondary,
        .btn-outline {
          padding: 0.55rem;
          font-size: 0.75rem;
        }
      }

      .help-section {
        padding-top: 1rem;

        h4 {
          font-size: 0.85rem;
        }

        .help-links {
          a {
            font-size: 0.8rem;
          }
        }
      }
    }
  }
`;

export default NotFound;
